/**
 * MapExportService - Servicio para exportación de vistas del mapa
 *
 * @version 1.0
 * @updated 2026-02-19
 * @module core/map-engine
 *
 * @description
 * Servicio singleton para exportar vistas del mapa como imágenes PNG de alta resolución
 * o PDFs con metadatos. Utiliza la capacidad de MapLibre de preserveDrawingBuffer.
 *
 * @remarks
 * **Características**:
 * - Exportación PNG con resolución configurable (1x, 2x, 3x)
 * - Exportación PDF con metadatos del proyecto
 * - Captura del canvas del mapa sin necesidad de bibliotecas externas
 * - Soporte para marcas de agua y overlays opcionales
 *
 * @example
 * ```typescript
 * import { mapExportService } from '@/core/map-engine/MapExportService';
 *
 * // Exportar PNG de alta resolución
 * await mapExportService.exportPNG({
 *   filename: 'mapa-bahia-blanca',
 *   scale: 2, // 2x resolución
 *   includeWatermark: true
 * });
 *
 * // Exportar PDF
 * await mapExportService.exportPDF({
 *   filename: 'reporte-ambiental',
 *   title: 'Reporte Ambiental - San Juan',
 *   description: 'Estado actual de la forestación urbana'
 * });
 * ```
 */

import type { Map as MapLibreMap } from 'maplibre-gl';
import { jsPDF } from 'jspdf';

/**
 * Opciones para exportación PNG
 */
export interface PNGExportOptions {
  /**
   * Nombre del archivo (sin extensión)
   * @default 'map-export'
   */
  filename?: string;

  /**
   * Escala de resolución (1x = normal, 2x = retina, 3x = ultra HD)
   * @default 2
   */
  scale?: 1 | 2 | 3;

  /**
   * Incluir marca de agua "Generado con WESTEER"
   * @default true
   */
  includeWatermark?: boolean;

  /**
   * Calidad JPEG (0.0 - 1.0, solo si format = 'jpeg')
   * @default 0.95
   */
  quality?: number;

  /**
   * Formato de salida
   * @default 'png'
   */
  format?: 'png' | 'jpeg';
}

/**
 * Opciones para exportación PDF
 */
export interface PDFExportOptions {
  /**
   * Nombre del archivo (sin extensión)
   * @default 'map-export'
   */
  filename?: string;

  /**
   * Título del documento PDF
   */
  title?: string;

  /**
   * Descripción del documento PDF
   */
  description?: string;

  /**
   * Orientación de la página
   * @default 'landscape'
   */
  orientation?: 'portrait' | 'landscape';

  /**
   * Tamaño de página
   * @default 'a4'
   */
  pageSize?: 'a4' | 'letter' | 'legal';

  /**
   * Incluir metadatos del proyecto
   * @default true
   */
  includeMetadata?: boolean;
}

/**
 * Servicio de exportación de mapas
 */
export class MapExportService {
  private map: MapLibreMap | null = null;

  /**
   * Registra la instancia del mapa
   */
  setMap(map: MapLibreMap): void {
    this.map = map;
    console.log('[MapExportService] Mapa registrado');
  }

  /**
   * Obtiene la instancia del mapa
   */
  getMap(): MapLibreMap | null {
    return this.map;
  }

  /**
   * Exporta el mapa actual como PNG
   *
   * @remarks
   * Captura el canvas del mapa usando MapLibre's preserveDrawingBuffer.
   * Aplica escala para aumentar resolución sin cambiar el zoom visual.
   */
  async exportPNG(options: PNGExportOptions = {}): Promise<void> {
    const {
      filename = 'map-export',
      scale = 2,
      includeWatermark = true,
      quality = 0.95,
      format = 'png',
    } = options;

    if (!this.map) {
      throw new Error('[MapExportService] No map instance available');
    }

    console.log(`[MapExportService] Exportando mapa como ${format.toUpperCase()} (escala: ${scale}x)`);

    try {
      // Obtener el canvas del mapa
      const mapCanvas = this.map.getCanvas();

      // Crear un nuevo canvas con la escala aumentada
      const scaledCanvas = document.createElement('canvas');
      const width = mapCanvas.width * scale;
      const height = mapCanvas.height * scale;
      scaledCanvas.width = width;
      scaledCanvas.height = height;

      const ctx = scaledCanvas.getContext('2d');
      if (!ctx) {
        throw new Error('[MapExportService] No se pudo obtener contexto 2D del canvas');
      }

      // Dibujar el mapa escalado
      ctx.scale(scale, scale);
      ctx.drawImage(mapCanvas, 0, 0);
      ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transform

      // Agregar marca de agua si está habilitada
      if (includeWatermark) {
        this.addWatermark(ctx, width, height);
      }

      // Convertir canvas a Blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        scaledCanvas.toBlob(
          (b) => {
            if (b) resolve(b);
            else reject(new Error('Failed to create blob'));
          },
          format === 'jpeg' ? 'image/jpeg' : 'image/png',
          quality
        );
      });

      // Descargar el archivo
      this.downloadBlob(blob, `${filename}.${format}`);

      console.log(`[MapExportService] Exportación ${format.toUpperCase()} completada: ${filename}.${format}`);
    } catch (error) {
      console.error('[MapExportService] Error al exportar PNG:', error);
      throw error;
    }
  }

  /**
   * Exporta el mapa actual como PDF
   *
   * @remarks
   * Usa jsPDF para generar un PDF con la imagen del mapa
   * y metadatos opcionales del proyecto.
   */
  async exportPDF(options: PDFExportOptions = {}): Promise<void> {
    const {
      filename = 'map-export',
      title = 'Mapa WESTEER',
      description = '',
      orientation = 'landscape',
      pageSize = 'a4',
      includeMetadata = true,
    } = options;

    if (!this.map) {
      throw new Error('[MapExportService] No map instance available');
    }

    console.log('[MapExportService] Exportando mapa como PDF');

    try {
      // Obtener imagen del mapa como PNG
      const mapCanvas = this.map.getCanvas();
      const imgData = mapCanvas.toDataURL('image/png', 1.0);

      // Crear documento PDF
      const pdf = new jsPDF({
        orientation,
        unit: 'mm',
        format: pageSize,
      });

      // Dimensiones de la página
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Calcular dimensiones de la imagen manteniendo aspect ratio
      const imgAspectRatio = mapCanvas.width / mapCanvas.height;
      const pageAspectRatio = pageWidth / pageHeight;

      let imgWidth: number;
      let imgHeight: number;
      let xOffset = 0;
      let yOffset = 0;

      if (imgAspectRatio > pageAspectRatio) {
        // Imagen más ancha que la página
        imgWidth = pageWidth;
        imgHeight = pageWidth / imgAspectRatio;
        yOffset = (pageHeight - imgHeight) / 2;
      } else {
        // Imagen más alta que la página
        imgHeight = pageHeight;
        imgWidth = pageHeight * imgAspectRatio;
        xOffset = (pageWidth - imgWidth) / 2;
      }

      // Agregar imagen al PDF
      pdf.addImage(imgData, 'PNG', xOffset, yOffset, imgWidth, imgHeight);

      // Agregar metadatos si está habilitado
      if (includeMetadata) {
        pdf.setProperties({
          title,
          subject: description,
          author: 'WESTEER',
          creator: 'WESTEER Map Engine',
        });

        // Agregar título y descripción como texto
        if (title || description) {
          pdf.addPage();
          pdf.setFontSize(16);
          pdf.text(title, 10, 20);

          if (description) {
            pdf.setFontSize(12);
            const descLines = pdf.splitTextToSize(description, pageWidth - 20);
            pdf.text(descLines, 10, 30);
          }
        }
      }

      // Descargar PDF
      pdf.save(`${filename}.pdf`);

      console.log(`[MapExportService] Exportación PDF completada: ${filename}.pdf`);
    } catch (error) {
      console.error('[MapExportService] Error al exportar PDF:', error);
      throw error;
    }
  }

  /**
   * Agrega marca de agua al canvas
   */
  private addWatermark(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    const text = 'Generado con WESTEER';
    const fontSize = Math.max(12, width / 80); // Escalar tamaño de fuente con el canvas

    ctx.save();

    // Configurar estilo de texto
    ctx.font = `${fontSize}px Inter, sans-serif`;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';

    // Dibujar texto en esquina inferior derecha
    const padding = fontSize;
    ctx.fillText(text, width - padding, height - padding);

    ctx.restore();
  }

  /**
   * Descarga un Blob como archivo
   */
  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Obtiene información del mapa actual (para debugging)
   */
  getMapInfo(): {
    center: [number, number] | null;
    zoom: number | null;
    bearing: number | null;
    pitch: number | null;
  } {
    if (!this.map) {
      return { center: null, zoom: null, bearing: null, pitch: null };
    }

    return {
      center: [this.map.getCenter().lng, this.map.getCenter().lat],
      zoom: this.map.getZoom(),
      bearing: this.map.getBearing(),
      pitch: this.map.getPitch(),
    };
  }
}

/**
 * Instancia singleton del servicio
 */
export const mapExportService = new MapExportService();
